// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { ProductInfo } from "@insite/client-framework/Common/ProductInfo";
import { getUnitNetPrice } from "@insite/client-framework/Services/Helpers/ProductPriceService";
import { getSession } from "@insite/client-framework/Store/Context/ContextSelectors";
import { AvailabilityMessageType, ProductModel } from "@insite/client-framework/Types/ApiModels";
import getProductsActiveState from "@keller/App/Widgets/SearchEngineOptimization/ActiveState/Products";
import { SeoAppProps, SeoHandlerProps } from "@keller/App/Widgets/SearchEngineOptimization/Type";
import { Product, ProductCollection, WithContext } from "@keller/App/Widgets/SearchEngineOptimization/Type/schema";

/**
 * get availability url based on real time inventory
 * @param productInfo
 */
export const getAvailability = (productInfo: ProductInfo | undefined) => {
    if (!productInfo) {
        return "https://schema.org/OutOfStock";
    }

    const availability = productInfo.inventory?.inventoryAvailabilityDtos?.find(
        o => o.unitOfMeasure.toLowerCase() === (productInfo?.unitOfMeasure?.toLowerCase() || ""),
    )?.availability;

    switch (availability?.messageType) {
        case AvailabilityMessageType.Available:
            return "https://schema.org/InStock";
        case AvailabilityMessageType.LowStock:
            return "https://schema.org/InStock";
        case AvailabilityMessageType.OutOfStock:
            return "https://schema.org/OutOfStock";
        default:
            return "https://schema.org/OutOfStock";
    }
};

/**
 * Create Schema for Product
 * @param product
 * @param productInfo
 * @param options
 */
const createProductSchema = (
    product: ProductModel,
    productInfo?: ProductInfo,
    options?: {
        url?: string;
        currency?: string;
    },
): Product => {
    let productPrice = 0;
    if (productInfo?.pricing) {
        const priceDisplay = getUnitNetPrice(productInfo?.pricing, productInfo?.qtyOrdered || 1).priceDisplay;
        productPrice = Number((priceDisplay || "0").replace(/[^0-9\.-]+/g, ""));
    }

    const item: Product = {
        "@type": "Product",
        name: product.productTitle,
        description: product.content?.htmlContent.replace(/(<([^>]+)>)/gi, "") || product.productTitle,
        image: product.mediumImagePath,
        productID: product.productNumber,
        url: `${options?.url}${product.canonicalUrl || product.urlSegment}`,
        sku: product.detail?.sku || product.productNumber,
        mpn: product.manufacturerItem,
        gtin: product.properties?.gtin || product.properties?.ean || product.detail?.upcCode,
        offers: {
            "@type": "Offer",
            priceCurrency: options?.currency || "USD",
            url: `${options?.url}${product.canonicalUrl || product.urlSegment}`,
            price: productPrice,
            availability: getAvailability(productInfo),
        },
    };

    if (product.brand) {
        item.brand = {
            "@type": "Brand",
            name: product.brand?.name,
            url: `${options?.url}${product.brand?.detailPagePath}`,
            logo: product.brand?.logoLargeImagePath,
        };
    }

    if (product.attributeTypes && product.attributeTypes?.length > 0) {
        item.additionalProperty = [];

        product.attributeTypes.forEach(attributeType => {
            attributeType.attributeValues?.forEach(attributeValue => {
                const attributeName = attributeType.label || attributeType.name;
                (item.additionalProperty as Product["additionalProperty"][])!.push({
                    "@type": "PropertyValue",
                    name: attributeName.includes(":") ? attributeName.split(":")[1] : attributeName,
                    value: attributeValue.valueDisplay,
                });
            });
        });
    }

    return item;
};

/**
 * Build the structured data
 */
const loadSchemaProduct = ({
    state,
    payload,
    url,
    currentPage,
}: SeoAppProps): WithContext<Product | ProductCollection> => {
    const isProductDetailsPage = currentPage.type === "ProductDetailsPage";
    const currency = getSession(state).currency?.currencyCode;

    const products = payload.products;
    const productInfoList = isProductDetailsPage
        ? state.pages.productDetails.productInfosById
        : state.pages.productList.productInfosByProductId;

    if (!isProductDetailsPage) {
        const schemaData: WithContext<ProductCollection> = {
            "@context": "https://schema.org",
            "@type": "ProductCollection",
        };

        schemaData.mainEntity =
            products?.map(product =>
                createProductSchema(product, productInfoList?.[product.id], {
                    url,
                    currency,
                }),
            ) || [];

        return schemaData;
    }

    /**
     * For ProductDetails Page
     */

    let schemaData: WithContext<Product> = {
        "@context": "https://schema.org",
        "@type": "Product",
    };

    if (!products?.[0]) {
        return schemaData;
    }
    const productInfo = productInfoList?.[products[0].id];
    const productItemSchema = createProductSchema(products[0], productInfo, {
        url,
        currency,
    });

    schemaData = {
        ...schemaData,
        ...productItemSchema,
    };

    return schemaData;
};

export default {
    isActivate: getProductsActiveState,
    buildSchema: loadSchemaProduct,
} as SeoHandlerProps;
